import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6efkbeqd308q1a0ajk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <div className="text-teal-600 text-sm font-semibold tracking-wider uppercase mb-3">
              GET IN TOUCH
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Let's Build Your<br />Network Future
            </h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              Schedule a consultation with our experts to discuss your network infrastructure, cloud migration, or AI/ML training needs. We're here to help transform your technology landscape.
            </p>

            <div className="space-y-4">
              <div className="flex items-center p-5 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Email</div>
                  <div className="font-semibold text-gray-900">info@nexgentechus.com</div>
                </div>
              </div>

              <div className="flex items-center p-5 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Phone</div>
                  <div className="font-semibold text-gray-900">🇮🇳 +91 9999111288</div>
                  <div className="font-semibold text-gray-900">🇺🇸 +1 9402798351</div>
                </div>
              </div>

              <div className="flex items-start p-5 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5"></div>
                    <div className="font-semibold text-gray-900 text-sm">UG01, SG Impression, Raj Nagar Extension,<br />Ghaziabad, India 201011</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5"></div>
                    <div className="font-semibold text-gray-900 text-sm">2704, Dancing Flame Dr,<br />Denton TX 75061, USA</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5"></div>
                    <div className="font-semibold text-gray-900 text-sm">B102Z-F55-5509, Danube Bayz 102,<br />Business Bay, Dubai</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div>
            <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Interest *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Network Services">Network Services</option>
                  <option value="Consulting Services">Consulting Services</option>
                  <option value="Network Automation">Network Automation</option>
                  <option value="Data Center Solutions">Data Center Solutions</option>
                  <option value="AI/ML Training">AI/ML Training</option>
                  <option value="Gen-AI & RAG Training">Gen-AI & RAG Training</option>
                  <option value="Agentic-AI Training">Agentic-AI Training</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="Tell us about your project or requirements..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800 text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our privacy policy and terms of service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
